import {view, Action} from '@sail/ui';
import {useEffect, useRef, useState} from 'react';
import {DotComHeaderTitle} from './DotComHeaderTitle';
import {DotComMinimizeButton} from './DotComMinimizeButton';
import {CloseButton, SpeakerButton} from '../Header';
import {useWidget, useWidgetControls} from '../useWidget';
import {useTheme} from '../../ThemeProvider';
import {dotComColors} from '../../theme/dotCom';
import {useAnalytics} from '../../AnalyticsProvider';

type Props = {
  isCSAT: boolean;
};

export function DotComHeader({isCSAT}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const {setMinimizedHeight, toggle} = useWidgetControls();
  const analytics = useAnalytics();
  const theme = useTheme();

  const {header} = useWidget();
  const [showHeader, setShowHeader] = useState<boolean>(header.isVisible());
  const [headerDisabled, setHeaderDisabled] = useState<boolean>(
    header.isDisabled(),
  );

  useEffect(() => {
    const sub = header.visibilityObservable().subscribe(setShowHeader);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    const sub = header.disabledObservable().subscribe(setHeaderDisabled);
    return () => sub.unsubscribe();
  }, [header]);

  useEffect(() => {
    if (ref.current) {
      setMinimizedHeight(ref.current.offsetHeight);
    }
  }, [setMinimizedHeight]);

  if (!showHeader) {
    return null;
  }

  return (
    <view.div
      ref={ref}
      data-testid="conversations-widget-header"
      css={{
        stack: 'y',
        flexGrow: '0 !important',
        position: 'relative',
      }}
    >
      <Action
        tabIndex={-1}
        onPress={() => {
          analytics.action('toggle_minimize_header_clicked');
          toggle();
        }}
      >
        <view.div
          css={{
            userSelect: 'none',
            backgroundColor: theme.brandColor,
            paddingY: 'small',
            paddingX: 'medium',
            stack: 'x',
            alignY: 'center',
            maxHeight: '42px',
            flexGrow: '0',
          }}
        >
          {headerDisabled ? (
            <view.div
              data-testid="conversations-widget-header-disabled-dimmer"
              css={{
                position: 'absolute',
                zIndex: 10,
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
                opacity: '0.3',
                backgroundColor: '#000000',
              }}
            />
          ) : null}

          <DotComHeaderTitle isCSAT={isCSAT} />

          <view.div
            css={{
              stack: 'x',
              alignY: 'center',
              alignX: 'end',
              gap: '12px',
              height: 'fill',
            }}
          >
            <SpeakerButton
              disabled={headerDisabled}
              color={dotComColors.brand800}
              hoverColor={dotComColors.brand900}
              tooltipEnabled={false}
            />
            <DotComMinimizeButton
              disabled={headerDisabled}
              color={dotComColors.brand800}
              hoverColor={dotComColors.brand900}
            />
            <CloseButton
              disabled={headerDisabled}
              color={dotComColors.brand800}
              hoverColor={dotComColors.brand900}
              tooltipEnabled={false}
            />
          </view.div>
        </view.div>
      </Action>

      <view.div
        css={{
          height: '2px',
          background: theme.headerBarGradient,
        }}
      />
    </view.div>
  );
}
